import { type ComponentType, type LazyExoticComponent, lazy } from "react";

type ChunkModule<P> = { default: ComponentType<P> };

export type LazyChunk<P> = {
  readonly Component: LazyExoticComponent<ComponentType<P>>;
  preload: () => void;
};

// A promise whose then() calls back at once: React.lazy then renders a module
// that is already loaded in the same pass, without a fallback commit.
function settled<T>(value: T): Promise<T> {
  return Object.assign(Promise.resolve(value), {
    then(onFulfilled?: ((loaded: T) => unknown) | null) {
      return new Promise(resolve =>
        resolve(onFulfilled ? onFulfilled(value) : value),
      );
    },
  });
}

function Unavailable() {
  return null;
}

/**
 * A component that loads with `load()` on first render or on `preload()`.
 * A failed load renders nothing and logs one warning per chunk; the content
 * stays empty until a page reload (browsers keep a failed import).
 */
export function lazyChunk<P>(
  load: () => Promise<ChunkModule<P>>,
  name: string,
): LazyChunk<P> {
  let loaded: ChunkModule<P> | null = null;
  let pending: Promise<ChunkModule<P>> | null = null;

  const start = () => {
    pending ??= load().then(
      chunk => {
        loaded = chunk;
        return chunk;
      },
      (error: unknown) => {
        console.warn("[pix] chunk", name, error);
        return { default: Unavailable };
      },
    );
    return pending;
  };

  const Component = lazy(() => (loaded ? settled(loaded) : start()));

  return {
    Component,
    preload() {
      void start();
    },
  };
}
